import { difference, differenceBy, find, includes, reduce, uniq } from 'es-toolkit/compat';
import type { SlateImage, SlateImageInput } from 'generated/graphql';
import type { SlateFormValue, SlateListFormValues } from './slates.types';

/**
 * Check if a slate marked as dirty has actually changed.
 * @param savedSlate
 * @param dirtySlate
 */
export function hasDirtySlateChanged(savedSlate: SlateImage, dirtySlate: SlateFormValue): boolean {
  return (
    savedSlate.assetId !== dirtySlate.assetId ||
    savedSlate.variant !== dirtySlate.variant ||
    // "removed" is not a form value. The mere presence of a slate row in the form implies removed = false.
    // Therefore, if the saved slate was removed then it implies a change of removed = true to removed = false
    Boolean(savedSlate.removed)
  );
}

/**
 * Based on the changes the user has made to the slates on the UI, determine the slates that must be saved.
 * It only returns slates that have actually changed.
 * @param dirtyFields
 * @param formVals
 * @param savedSlates the slates that this client knows were saved
 */
export function getSlatesToSave({
  dirtyFields,
  formVals,
  savedSlates,
}: {
  dirtyFields: string[];
  formVals: SlateListFormValues;
  savedSlates: SlateImage[];
}): SlateImageInput[] {
  const slateNameRegex = /slates\[(\d+)]/;
  const dirtyPrefixes = uniq(dirtyFields.map((field) => field.split('.')[0]));
  const dirtySlates = reduce(
    dirtyPrefixes,
    (slates, dirtyPrefix) => {
      const dirtyPrefixMatches = dirtyPrefix.match(slateNameRegex);
      if (!dirtyPrefixMatches) {
        return slates;
      }

      const slateIndex = parseInt(dirtyPrefixMatches[1], 10);
      const slateFormValue = formVals.slates[slateIndex];

      // find the version for this variant if it has been saved already
      const savedSlate = find(savedSlates, (slate) => slate.variant === slateFormValue.variant);

      // check that it has truly changed. Dirtiness doesn't guarantee that it has changed
      if (savedSlate && !hasDirtySlateChanged(savedSlate, slateFormValue)) {
        return slates;
      }

      const version = savedSlate ? savedSlate.version : 0; // if it's new, pass version 0

      return [
        ...slates,
        {
          ...slateFormValue,
          removed: false,
          version,
        },
      ];
    },
    [] as SlateImageInput[],
  );

  // if they changed the variant of a slate, remove the old variant and create the new variant
  const savedVariants = savedSlates.filter((slate) => !slate.removed).map((slate) => slate.variant);
  const proposedVariants = (formVals.slates ?? []).map((slate) => slate.variant);
  const removedVariants = difference(savedVariants, proposedVariants);
  const removedSlates = savedSlates
    .filter((slate) => includes(removedVariants, slate.variant))
    .map(
      (slate): SlateImageInput => ({
        ...slate,
        removed: true,
      }),
    );

  return [
    ...dirtySlates,
    ...removedSlates,
  ].map(
    (slate): SlateImageInput => ({
      assetId: slate.assetId,
      removed: slate.removed,
      url: slate.url,
      variant: slate.variant,
      version: slate.version,
    }),
  );
}

/**
 * Given a saveSlates mutation response, determine the new state of the cached slates. The cache should include
 * any existing slates that were not edited by the mutation.
 * @param cachedSlates
 * @param mutationResult
 */
export function getUpdatedSlates({
  cachedSlates,
  succeededSlates,
}: {
  cachedSlates: SlateImage[];
  succeededSlates: SlateImage[];
}): SlateImage[] {
  // replace the slates in the cache with the new data for the variants
  const newSlates = differenceBy(succeededSlates, cachedSlates, (s) => s.variant);
  const updatedSlates = cachedSlates.map((slate) => {
    const mutatedSlate = find(succeededSlates, (s) => s.variant === slate.variant);
    if (mutatedSlate) {
      return {
        ...slate,
        ...mutatedSlate,
      };
    }
    return slate;
  });

  return [
    ...newSlates,
    ...updatedSlates,
  ];
}

export function getShipmentStatusUrl(shipmentId: string | undefined): string {
  if (!shipmentId) {
    return '';
  }
  return `https://console.harmony.a2z.com/wharf/shipment/${shipmentId}`;
}
